<template>
  <v-container fluid class="ap-page">
    <!-- ================= Header ================= -->
    <div class="ap-header">
      <div>
        <h1 class="ap-title">Approve Time Off</h1>
        <div class="ap-subtitle">Review and manage employee leave requests</div>
      </div>
      <v-btn outlined depressed color="#2A6F97" class="ap-btn-outline" @click="openApporverHistory">
        <v-icon left size="20">mdi-history</v-icon>
        View history
      </v-btn>
    </div>

    <!-- ================= Summary ================= -->
    <div class="ap-card ap-summary">
      <div class="ap-stat">
        <div class="ap-stat-icon">
          <v-icon size="28" color="#2A6F97">mdi-account-group-outline</v-icon>
        </div>
        <div>
          <div class="ap-stat-label">Pending requests</div>
          <div class="ap-stat-value">{{ pendingCount }}</div>
        </div>
      </div>
      <div class="ap-stat-sep"></div>
      <div class="ap-stat">
        <div class="ap-stat-icon">
          <v-icon size="28" color="#2A6F97">mdi-office-building-outline</v-icon>
        </div>
        <div>
          <div class="ap-stat-label">Departments</div>
          <div class="ap-stat-value">{{ departmentOptions.length - 1 }}</div>
        </div>
      </div>
    </div>

    <!-- ================= Filters ================= -->
    <div class="ap-card ap-filters">
      <v-text-field v-model="search" outlined dense hide-details clearable placeholder="Search employee"
        prepend-inner-icon="mdi-magnify" class="ap-filter ap-filter-search" @input="search_data"
        @click:clear="$nextTick(search_data)"></v-text-field>

      <v-select v-model="filterDepartment" :items="departmentOptions" outlined dense hide-details
        prepend-inner-icon="mdi-office-building-outline" class="ap-filter ap-filter-dep" :menu-props="{ offsetY: true }"
        @change="search_data"></v-select>

      <v-select v-model="filterType" :items="leaveTypeOptions" outlined dense hide-details
        class="ap-filter ap-filter-type" :menu-props="{ offsetY: true }" @change="search_data"></v-select>

      <div class="ap-date-wrap">
        <div class="ap-date-label">From</div>
        <v-menu v-model="menu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="computeddatefromFormatted" placeholder="DD/MM/YYYY" append-icon="mdi-calendar"
              readonly v-bind="attrs" v-on="on" outlined dense hide-details clearable class="ap-filter ap-filter-date"
              @click:clear="$nextTick(() => { datefrom = null; dateto = null; search_data(); })"></v-text-field>
          </template>
          <v-date-picker v-model="datefrom" no-title @input="menu = false" @change="search_data"></v-date-picker>
        </v-menu>
      </div>

      <div class="ap-date-wrap">
        <div class="ap-date-label">To</div>
        <v-menu v-model="menu2" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="computeddateToFormatted" placeholder="DD/MM/YYYY" append-icon="mdi-calendar" readonly
              v-bind="attrs" v-on="on" outlined dense hide-details clearable class="ap-filter ap-filter-date"
              @click:clear="$nextTick(() => { datefrom = null; dateto = null; search_data(); })"></v-text-field>
          </template>
          <v-date-picker v-model="dateto" no-title @input="menu2 = false" @change="search_data"></v-date-picker>
        </v-menu>
      </div>

      <a class="ap-link ap-reset" @click="resetFilters">
        <v-icon size="20" color="#2A6F97">mdi-restore</v-icon>
        <span class="ml-1">Reset</span>
      </a>
    </div>

    <!-- ================= Table ================= -->
    <div class="ap-card ap-table-card">
      <div class="ap-table-wrap">
        <table class="ap-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Leave type</th>
              <th>Date</th>
              <th>Duration</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody v-if="paginatedItems.length > 0">
            <tr v-for="item in paginatedItems" :key="item.id" class="ap-row"
              @click="goToLeaveApproveById(item.id, item.userid, item.status)">
              <td>
                <div class="d-flex align-center">
                  <div class="ap-avatar" :style="{ background: avatarColor(employeeName(item)).bg }">
                    <img v-if="item.image_profile" :src="item.image_profile" @error="item.image_profile = ''" />
                    <span v-else :style="{ color: avatarColor(employeeName(item)).fg }">
                      {{ getInitials(employeeName(item)) }}
                    </span>
                  </div>
                  <span class="ml-4">{{ employeeName(item) }}</span>
                </div>
              </td>
              <td>{{ departmentName(item) || "-" }}</td>
              <td>{{ item.leave_type || "-" }}</td>
              <td>{{ leaveDateText(item) }}</td>
              <td>{{ formatDuration(item.hours_amount) }}</td>
              <td>
                <span class="ap-chip" :class="statusChipClass(item)">{{ statusText(item) }}</span>
              </td>
              <td>
                <a class="ap-link" @click.stop="goToLeaveApproveById(item.id, item.userid, item.status)">
                  {{ isReviewed(item) ? "View details" : "Review" }}
                  <v-icon size="20" color="#2A6F97">mdi-chevron-right</v-icon>
                </a>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="7" class="ap-empty">
                {{ leavehistory_list.length > 0 ? "No requests match your filters" : "No Approve Time Off" }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ================= Pagination ================= -->
    <div class="ap-card ap-pagination">
      <div class="ap-muted">
        Showing {{ showingFrom }}–{{ showingTo }} of {{ datashowAll.length }}
        {{ datashowAll.length === 1 ? "request" : "requests" }}
      </div>

      <div class="ap-pager">
        <button class="ap-page-btn" :disabled="page === 1" @click="previousPage">
          <v-icon size="20">mdi-chevron-left</v-icon>
        </button>
        <template v-for="(p, i) in pageButtons">
          <span v-if="p === '...'" :key="'e' + i" class="ap-page-ellipsis">…</span>
          <button v-else :key="'p' + p" class="ap-page-btn" :class="{ 'ap-page-active': p === page }" @click="page = p">
            {{ p }}
          </button>
        </template>
        <button class="ap-page-btn" :disabled="page === pageCount" @click="nextPage">
          <v-icon size="20">mdi-chevron-right</v-icon>
        </button>
      </div>

      <div class="d-flex align-center">
        <span class="ap-muted mr-3">Rows per page:</span>
        <v-select v-model="itemsPerPage" :items="rowsPerPageOptions" outlined dense hide-details
          class="ap-filter ap-rows-select" :menu-props="{ offsetY: true, top: true }"></v-select>
      </div>
    </div>
  </v-container>
</template>

<script>
import SuccessDialog from "@/components/cards/SuccessDialog";
import api from "@/services/api";
import { server, imageUrl } from "@/services/constants";
import { language } from "../language/Leavelist.js";
import unity from "@/unity/unity";
import moment from "moment";

const ALL_DEPARTMENTS = "All departments";
const ALL_TYPES = "All leave types";

const AVATAR_COLORS = [
  { bg: "#E6F2F8", fg: "#2A6F97" },
  { bg: "#ECE9FB", fg: "#4B3BA8" },
  { bg: "#FDF1E3", fg: "#8A4B0F" },
  { bg: "#E6F6EE", fg: "#1E7A4C" },
  { bg: "#FDE8EA", fg: "#B4232F" },
  { bg: "#E8F4FA", fg: "#1D6A8A" },
  { bg: "#FBE7DF", fg: "#A2401C" },
];

export default {
  name: "leave-list",
  data(vm) {
    return {
      authorize: [],
      search: "",
      menu: false,
      menu2: false,
      selectstatus: "",
      dataAll: [],
      status: ["Draft", "Submit"],
      datefrom: null,
      dateto: null,
      dateCurrent: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      seticonsearchmore: { icon: "mdi mdi-chevron-down", action: false },
      setlanguage: language.en,
      defaultlanguage: "en",
      chooselanguage: [
        { name: "en", flag: "gb" },
        { name: "th", flag: "th" },
      ],

      page: 1, // หน้าเริ่มต้น
      itemsPerPage: 20, // จำนวนรายการต่อหน้า
      rowsPerPageOptions: [10, 20, 50, 100],
      leavehistory_list: [],
      datashowAll: [],
      // ===== ตัวกรองใหม่ =====
      filterDepartment: ALL_DEPARTMENTS,
      filterType: ALL_TYPES,
    };
  },
  async beforeCreate() {
    this.$store.state.navMenu = true;
  },
  computed: {
    computeddatefromFormatted() {
      return unity.formatDate(this.datefrom);
    },
    computeddateToFormatted() {
      return unity.formatDate(this.dateto);
    },
    itemsForSelected() {
      if (this.search.length) {
        return this.dataSource.filter((item) => this.search.includes(item));
      }
      return this.dataSource;
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.datashowAll.length / this.itemsPerPage)); // คำนวณจำนวนหน้าทั้งหมด
    },
    paginatedItems() {
      const start = (this.page - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.datashowAll.slice(start, end); // ดึงรายการที่ต้องแสดงในแต่ละหน้า
    },

    // ===== UI ใหม่ =====
    pendingCount() {
      return this.leavehistory_list.filter((i) => !this.isReviewed(i)).length;
    },
    departmentOptions() {
      const deps = [
        ...new Set(this.leavehistory_list.map((i) => this.departmentName(i)).filter(Boolean)),
      ].sort();
      return [ALL_DEPARTMENTS, ...deps];
    },
    leaveTypeOptions() {
      const types = [...new Set(this.leavehistory_list.map((i) => i.leave_type).filter(Boolean))].sort();
      return [ALL_TYPES, ...types];
    },
    showingFrom() {
      return this.datashowAll.length === 0 ? 0 : (this.page - 1) * this.itemsPerPage + 1;
    },
    showingTo() {
      return Math.min(this.page * this.itemsPerPage, this.datashowAll.length);
    },
    pageButtons() {
      const total = this.pageCount;
      const cur = this.page;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const pages = [1];
      const start = Math.max(2, cur - 1);
      const end = Math.min(total - 1, cur + 1);
      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < total - 1) pages.push("...");
      pages.push(total);
      return pages;
    },
  },
  watch: {
    itemsPerPage() {
      this.page = 1;
    },
  },
  async mounted() {
    await api.checkVersion();
    this.userId = localStorage.getItem(server.USER_ID);
    let yourUrlString = window.location;

    let parser = document.createElement("a");
    parser.href = yourUrlString;

    this.authorize_id = localStorage.getItem(server.AUTHORIZE_ID);

    if (this.authorize_id == null || this.authorize_id == 0) {
      this.$store.state.global_dialog = true;
      this.setupAlertDialog(
        true,
        "Authorize Failed",
        "Please Logout And Login Again",
        "text-h5 red--text text-center",
      );
      this.$router.back();
    }

    const router_path = parser.pathname.replace("/", "");

    const res_auth = await api.getAuthorize(this.userId, router_path);

    this.authorize_view = res_auth.data[0].smd_view >= 1 ? true : false;
    this.authorize_add = res_auth.data[0].smd_add >= 1 ? true : false;
    this.authorize_edit = res_auth.data[0].smd_edit >= 1 ? true : false;
    this.authorize_del = res_auth.data[0].smd_del >= 1 ? true : false;

    if (!this.authorize_view) {
      this.$router.push("/not-found");
    }

    // ----------------- Check Authorize ---------------------------

    let comp_id = localStorage.getItem(server.COMPANYID);

    this.userId = localStorage.getItem(server.USER_ID);
    if (this.userId && api.isLoggedIn()) {
      // this.$router.push("/");
    } else {
      this.$store.state.isLogged = false;
      this.$router.push("/login");
    }
    await this.loaddataLeaveRequest(this.userId);
    await this.loadleaveHistory(this.userId);
    this.$hideLoader();
  },
  methods: {
    openApporverHistory() {
      this.$router.push("/approver-time-off-history");
    },
    async loadleaveHistory() {
      var res_leave_history = [];
      if (localStorage.getItem("dtime_role") == "ADMIN") {
        res_leave_history = await api.getListAdminAll(
          localStorage.getItem(server.COMPANYID)
        );
      } else {
        res_leave_history = await api.getListApproveAll(
          localStorage.getItem(server.USER_ID),
        );
      }
      console.log(res_leave_history.data)

      for (const [index, item] of res_leave_history.data.entries()) {
        if (item.leave_type == "Sick Leave") {
          item.leave_type = "Sick";
        } else if (item.leave_type == "Vacation Leave") {
          item.leave_type = "Vacation";
        }
        var leave_day = 0;
        var leave_hours = 0;

        leave_day = Math.floor(Number(item.hours_amount) / 8);
        leave_hours = Number(item.hours_amount) - leave_day * 8;

        if (leave_day == 0) {
          if (leave_hours == 0) {
            item.description = `${item.data_date}`;
          } else {
            item.description = `${item.data_date} (${leave_hours} hours)`;
          }
        } else {
          if (leave_hours == 0) {
            item.description = `${item.data_date} (${leave_day} day)`;
          } else {
            item.description = `${item.data_date} (${leave_day} day ${leave_hours} hours)`;
          }
        }

        if (item.image != "" && item.image != null) {
          item.image_profile = imageUrl + "/" + item.image;
        } else {
          item.image_profile = "";
        }
      }
      this.leavehistory_list = res_leave_history.data;
      this.datashowAll = res_leave_history.data;
    },
    async goToLeaveApproveById(id, user_id, status) {
      if (Number(status) == 3 || Number(status) == 4) {
        this.$router.push(`/leave-request-waiting/${id}/leave`);
      } else {
        this.$router.push(`/approve-leave-confirm/${id}/${user_id}`);
      }
    },
    // ค้นหา (เงื่อนไขเดิม: ชื่อ/ประเภทลา + ช่วงวันที่) + เพิ่มกรองแผนก/ประเภทลา
    async search_data() {
      const searchTerm = String(this.search || "").toLowerCase();
      const text = (v) => String(v || "").toLowerCase();

      this.datashowAll = this.leavehistory_list.filter((item) => {
        const matchSearch =
          text(item.name_eng).includes(searchTerm) ||
          text(item.name_th).includes(searchTerm) ||
          text(item.leave_type).includes(searchTerm);

        const matchDate =
          this.datefrom && this.dateto
            ? this.datefrom <= item.str_time && this.dateto >= item.end_time
            : true;

        const matchDep =
          this.filterDepartment === ALL_DEPARTMENTS || this.departmentName(item) === this.filterDepartment;

        const matchType = this.filterType === ALL_TYPES || item.leave_type === this.filterType;

        return matchSearch && matchDate && matchDep && matchType;
      });
      this.page = 1;
    },
    resetFilters() {
      this.search = "";
      this.datefrom = null;
      this.dateto = null;
      this.filterDepartment = ALL_DEPARTMENTS;
      this.filterType = ALL_TYPES;
      this.search_data();
    },
    previousPage() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },
    nextPage() {
      if (this.page < this.pageCount) {
        this.page += 1;
      }
    },

    // ================= UI helpers (ใหม่) =================
    employeeName(item) {
      const en = String(item.name_eng || "").trim();
      const th = String(item.name_th || "").trim();
      return en || th || "-";
    },
    // ชื่อแผนก (รองรับหลายชื่อ field จาก API)
    departmentName(item) {
      return (
        item.department_name || item.dep_name || item.department || item.dept_name || item.departmentname || ""
      );
    },
    getInitials(name) {
      const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
      if (!parts.length || name === "-") return "?";
      return ((parts[0][0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
    },
    // สี avatar คงที่ตามชื่อ (คนเดิมได้สีเดิมทุกครั้ง)
    avatarColor(name) {
      let h = 0;
      const s = String(name || "");
      for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
      return AVATAR_COLORS[h % AVATAR_COLORS.length];
    },
    leaveDateText(item) {
      const start = item.str_time ? moment(item.str_time, "YYYY-MM-DD") : null;
      const end = item.end_time ? moment(item.end_time, "YYYY-MM-DD") : null;
      if (!start || !start.isValid()) return item.data_date || "-";
      if (!end || !end.isValid() || end.isSame(start, "day")) return start.format("D MMM YYYY");
      return `${start.format("D MMM YYYY")} - ${end.format("D MMM YYYY")}`;
    },
    formatDuration(hoursAmount) {
      const total = Number(hoursAmount) || 0;
      if (total <= 0) return "-";
      const d = Math.floor(total / 8);
      const h = Number((total - d * 8).toFixed(2));
      const parts = [];
      if (d > 0) parts.push(`${d} ${d === 1 ? "day" : "days"}`);
      if (h > 0) parts.push(`${h} ${h === 1 ? "hour" : "hours"}`);
      return parts.join(" ");
    },
    // อนุมัติ/ปฏิเสธแล้ว (เงื่อนไขเดียวกับ goToLeaveApproveById: status 3, 4)
    isReviewed(item) {
      const s = String(item.status_name || "").toLowerCase();
      return Number(item.status) == 3 || Number(item.status) == 4 || s === "approved" || s === "rejected";
    },
    statusText(item) {
      if (item.status_name == "Approved" || item.status_name == "Rejected") return item.status_name;
      return "Pending";
    },
    statusChipClass(item) {
      if (item.status_name == "Approved") return "ap-chip-success";
      if (item.status_name == "Rejected") return "ap-chip-danger";
      return "ap-chip-warning";
    },

    async loaddataLeaveRequest(id) {
      const result = await api.getListApproveIdNow(id);
      this.mDataArray = result.data;

      this.mDataArray.forEach((item) => {
        var tmp = {
          id: 13,
          employee_id: "163",
          startDate: "2022-06-20T01:30:00.000Z",
          endDate: "2022-06-20T10:30:00.000Z",
          startDate2: "2022-06-20 08:30:00",
          endDate2: "2022-06-20 17:30:00",
          amount: "8.00",
          leaveTypeId: 2,
          leave_type: "ลากิจ",
          description: "test emp",
          description_approve: "test approve emp",
          approve_id: 1,
          approve_name: "Request",
          approver_level1: 80,
          fullName: "นาย Test Employee",
          image: "",
        };
        tmp.id = item.id;
        tmp.employee_id = item.employee_id;
        tmp.startDate = item.startDate;
        tmp.endDate = item.endDate;
        tmp.startDate2 = item.startDate2;
        tmp.endDate2 = item.endDate2;
        tmp.startDateS = item.startDateS;
        tmp.endDateS = item.endDateS;
        tmp.amount = item.amount;
        tmp.amount_show = this.quotaListConvert(item.amount / 8);
        tmp.leaveTypeId = item.leave_type_id;
        tmp.leave_type = item.leave_type;
        tmp.description = item.description;
        tmp.description_approve = item.description_approve;
        tmp.approve_id = item.approve_id;
        tmp.approve_name = item.approve_name;
        tmp.approver_level1 = item.approver_level1;
        tmp.fullName = item.fullName;

        if (item.image == "" || item.image == null) {
          tmp.image = imageUrl + "/personal.png";
        } else {
          tmp.image = imageUrl + "/" + item.image;
        }

        this.dataAll.push(tmp);
      });
    },
    quotaListConvert: function (H) {
      var d = 0;
      var h = 0;
      var m = 0;
      var tmp1 = parseFloat(H * 480);
      var tmp_final;

      var tmp_d = tmp1 / 480;

      d = Math.floor(tmp_d);
      h = Math.floor((tmp_d - d) * 8);
      m = Math.floor(((tmp_d - d) * 8 - h) * 60);

      tmp_final = `${d} Days ${h} Hour ${m} Minute`;

      return tmp_final;
    },
  },
};
</script>

<style scoped>
/* ================= Page ================= */
.ap-page {
  padding: 16px 32px 32px;
  color: #1F2937;
}

.ap-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.ap-title {
  font-size: 30px;
  font-weight: 700;
  line-height: 1.2;
  color: #111827;
  margin: 0;
}

.ap-subtitle {
  font-size: 15px;
  color: #4B5563;
  margin-top: 4px;
}

.ap-btn-outline {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  height: 42px !important;
  padding: 0 18px !important;
  border-radius: 6px !important;
  background: #FFFFFF;
}

/* ================= Card ================= */
.ap-card {
  background: #FFFFFF;
  border: 1px solid #E8ECF1;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

/* ================= Summary ================= */
.ap-summary {
  display: flex;
  align-items: stretch;
  padding: 20px 28px;
  gap: 24px;
  margin-bottom: 16px;
}

.ap-stat {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
}

.ap-stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #EAF4F8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ap-stat-label {
  font-size: 14px;
  color: #4B5563;
}

.ap-stat-value {
  font-size: 26px;
  font-weight: 700;
  color: #2A6F97;
  line-height: 1.25;
}

.ap-stat-sep {
  width: 1px;
  background: #E5E7EB;
}

/* ================= Filters ================= */
.ap-filters {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 14px;
  padding: 16px 18px;
  margin-bottom: 16px;
}

.ap-filter {
  background: #FFFFFF;
  font-size: 14px;
}

.ap-filter>>>.v-input__slot {
  min-height: 42px !important;
  border-radius: 6px !important;
}

.ap-filter>>>fieldset {
  border-color: #D1D5DB !important;
}

.ap-filter.v-input--is-focused>>>fieldset {
  border-color: #2A6F97 !important;
}

.ap-filter-search {
  flex: 1 1 220px;
  min-width: 200px;
}

.ap-filter-dep {
  flex: 0 1 220px;
}

.ap-filter-type {
  flex: 0 1 190px;
}

.ap-date-wrap {
  flex: 0 1 160px;
}

.ap-date-label {
  font-size: 12px;
  color: #374151;
  margin-bottom: 2px;
}

.ap-rows-select {
  width: 84px;
  flex: none;
}

.ap-link {
  color: #2A6F97 !important;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
}

.ap-link:hover {
  text-decoration: underline;
}

.ap-reset {
  height: 42px;
  padding: 0 6px;
}

/* ================= Table ================= */
.ap-table-card {
  overflow: hidden;
  margin-bottom: 16px;
}

.ap-table-wrap {
  overflow-x: auto;
}

.ap-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.ap-table thead th {
  background: #F7F9FC;
  color: #111827;
  font-weight: 600;
  font-size: 14px;
  text-align: left;
  padding: 14px 20px;
  border-bottom: 1px solid #E8ECF1;
  white-space: nowrap;
}

.ap-table tbody td {
  padding: 10px 20px;
  border-bottom: 1px solid #EEF1F4;
  color: #111827;
  white-space: nowrap;
}

.ap-table tbody tr:last-child td {
  border-bottom: none;
}

.ap-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.ap-row:hover {
  background-color: #F3F8FC;
}

.ap-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  flex-shrink: 0;
}

.ap-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ap-chip {
  display: inline-block;
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 13px;
  font-weight: 500;
}

.ap-chip-success {
  background: #E8F3EA;
  color: #2E7D32;
}

.ap-chip-danger {
  background: #FDE8E8;
  color: #C62828;
}

.ap-chip-warning {
  background: #FDF0DC;
  color: #E08A1E;
}

.ap-empty {
  text-align: center;
  padding: 48px 16px !important;
  font-size: 16px;
  color: #6B7280 !important;
}

/* ================= Pagination ================= */
.ap-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 24px;
}

.ap-muted {
  color: #4B5563;
  font-size: 14px;
}

.ap-pager {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ap-page-btn {
  min-width: 36px;
  height: 34px;
  padding: 0 8px;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  background: #FFFFFF;
  color: #111827;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ap-page-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

.ap-page-active {
  background: #2A6F97;
  border-color: #2A6F97;
  color: #FFFFFF;
}

.ap-page-ellipsis {
  color: #6B7280;
  padding: 0 2px;
}

/* ================= Responsive ================= */
@media (max-width: 960px) {
  .ap-summary {
    flex-direction: column;
  }

  .ap-stat {
    justify-content: flex-start;
  }

  .ap-stat-sep {
    display: none;
  }
}

@media (max-width: 600px) {
  .ap-page {
    padding: 8px 12px 24px;
  }

  .ap-filter-search,
  .ap-filter-dep,
  .ap-filter-type,
  .ap-date-wrap {
    flex: 1 1 100%;
  }

  .ap-pagination {
    justify-content: center;
  }
}
</style>